/**
 * FilterActions — action buttons rendered below the filter rows in
 * the Bulk Add Course Section filter panel.
 *
 * Contains two buttons:
 *   - "Add Condition" — appends a new empty filter row.  Disabled
 *     (with a tooltip explanation) when any existing row is incomplete.
 *     This forces the user to finish one condition before starting
 *     another, preventing accidental submissions with blank filters.
 *   - "Filter" — submits the filter form, transforming filter rows
 *     into the API payload and triggering the CDG query.
 *
 * Uses `useWatch` to reactively observe the filter array so the
 * disabled state updates in real time as the user fills in fields.
 */
import React from 'react'
import { Box, Tooltip } from '@mui/material'
import GenericButton from '../../../../../components/GenericButton'
import { CirclePlus } from 'lucide-react'
import { ButtonSize, ButtonType } from '../../../../../components/GenericButton/styles'

import { useFormContext, useWatch } from 'react-hook-form'

interface FilterActionsProps {
  onAddCondition: () => void
  onFilter: () => void
}

const FilterActions: React.FC<FilterActionsProps> = ({ onAddCondition, onFilter }) => {
  const { control } = useFormContext()
  const filters = useWatch({ control, name: 'filters' }) || []
  // Guard: block adding new rows until every existing row is complete.
  // This prevents the user from stacking empty conditions that would
  // be silently stripped on submit, which could be confusing.
  const hasIncompleteRow = filters.some(
    (f: { category?: string, operator?: string, date?: string }) =>
      !f?.category || !f?.operator || !f?.date
  )
  return (
    <Box sx={{ marginTop: 4, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', gap: 2 }}>
      <Tooltip
        title={hasIncompleteRow ? 'Please fill out all fields in every row before adding another condition.' : ''}
        arrow
        disableHoverListener={!hasIncompleteRow}
      >
        <span>
          <GenericButton
            buttonType={ButtonType.Secondary}
            buttonSize={ButtonSize.Medium}
            startIcon={<CirclePlus />}
            onClick={onAddCondition}
            label="Add Condition"
            disabled={hasIncompleteRow}
          />
        </span>
      </Tooltip>
      <GenericButton
        buttonType={ButtonType.Primary}
        buttonSize={ButtonSize.Medium}
        onClick={onFilter}
        label="Filter"
      />
    </Box>
  )
}

export default FilterActions
